/**
 * 認証状態フック（フェーズ3）。
 *
 * - 初回マウント時に、リダイレクトコールバック（?code=...）を処理してトークン取得
 * - 既存トークン（localStorage）があれば復元
 * - user / login / logout を提供
 */
import { useEffect, useState } from 'react';
import {
  decodeUser,
  handleRedirectCallback,
  isAuthConfigured,
  loadTokens,
  logout as doLogout,
  startLogin,
  type AuthUser,
} from './cognito';

export interface UseAuthResult {
  /** 認証機能が有効か（env 未設定なら false）。 */
  enabled: boolean;
  /** ログイン中のユーザー（未ログインなら null）。 */
  user: AuthUser | null;
  /** コールバック処理中などの初期化中フラグ。 */
  loading: boolean;
  /** ログイン開始（Hosted UI へ遷移）。 */
  login: () => void;
  /** ログアウト。 */
  logout: () => void;
}

export function useAuth(): UseAuthResult {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const enabled = isAuthConfigured();

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    let disposed = false;
    (async () => {
      // 1. リダイレクト戻り（?code=...）があれば交換
      const fromRedirect = await handleRedirectCallback();
      // 2. 無ければ既存トークンを復元
      const tokens = fromRedirect ?? loadTokens();
      if (!disposed) {
        setUser(tokens ? decodeUser(tokens) : null);
        setLoading(false);
      }
    })();
    return () => {
      disposed = true;
    };
  }, [enabled]);

  return {
    enabled,
    user,
    loading,
    login: () => void startLogin(),
    logout: () => doLogout(),
  };
}
